import { useEffect, useMemo } from 'react'
import type { SpellIssue } from '@/lib/spell-check/spell-check-state'
import { findSpellWordAtPoint } from '../handlers/find-spell-word-at-point'
import type { ClickableSpellWord } from '../types/clickable-spell-word'

/** Clicks on these belong to them, even where they sit over a frame: the popover and menus. */
const OWN_CLICKS = '[data-radix-popper-content-wrapper], [role="dialog"], [role="menu"]'

interface UseSpellCanvasClickOptions {
  /** Clicks on flagged words are only taken while the words are flagged: panel ready, underlines on. */
  enabled: boolean
  issues: readonly SpellIssue[]
  /** Opens the fix popover on one instance of one issue. */
  onOpen: (issueId: string, occurrenceIndex: number) => void
}

/**
 * Clicking a flagged word on the canvas opens its fix popover, as in the design demo, and the panel
 * highlights the matching row. The canvas sees the click first and would select the frame, so the
 * click is taken before it, but only when it lands on a flagged word; every other click goes on to
 * the canvas as usual.
 *
 * Listening on the document is subscribing to something outside React, so it is an effect.
 */
export function useSpellCanvasClick({ enabled, issues, onOpen }: UseSpellCanvasClickOptions) {
  const words = useMemo<ClickableSpellWord[]>(
    () =>
      issues.flatMap((issue) =>
        issue.occurrences.flatMap((occurrence, occurrenceIndex) =>
          occurrence.status === 'open' &&
          !occurrence.onPath &&
          occurrence.shapeId !== undefined &&
          occurrence.start !== undefined &&
          occurrence.end !== undefined
            ? [
                {
                  issueId: issue.id,
                  occurrenceIndex,
                  shapeId: occurrence.shapeId,
                  start: occurrence.start,
                  end: occurrence.end,
                },
              ]
            : []
        )
      ),
    [issues]
  )

  useEffect(() => {
    if (!enabled) return

    const handlePointerDown = (event: PointerEvent) => {
      if (event.button !== 0) return
      if (event.target instanceof Element && event.target.closest(OWN_CLICKS)) return

      const hit = findSpellWordAtPoint(words, event.clientX, event.clientY)

      if (!hit) return

      event.preventDefault()
      event.stopPropagation()
      onOpen(hit.issueId, hit.occurrenceIndex)
    }

    // Capture phase: ahead of the canvas, which would otherwise select the frame under the click.
    document.addEventListener('pointerdown', handlePointerDown, true)

    return () => document.removeEventListener('pointerdown', handlePointerDown, true)
  }, [enabled, words, onOpen])
}
